/**
 * auth.js — Authentication domain service.
 * Login, password reset, MFA, logout, invitation flows.
 */
import {
  toApiUrl,
  fetchWithAuthRetry,
  jsonFetchOptions,
  defaultHeaders,
  getCredentialsMode,
  parseJsonOrThrow,
  readApiErrorMessage,
  readApiErrorData,
  normalizePositiveSeconds,
  normalizeMfaMethod,
  setStoredTokens,
  setForcePasswordChangePending,
  recordLoginTime,
  clearStoredTokens,
  clearLoginTime,
  getStoredAccessToken,
  getStoredRefreshToken,
  debugApiLog,
  requestJson,
} from './_core';
import { clearAuthenticatedQueryCache } from '../queryClient';

export async function loginWithPassword(email, password) {
  const safeEmail = String(email || '').trim();
  const safePassword = String(password || '');
  if (!safeEmail || !safePassword) {
    throw new Error('Email and password are required.');
  }

  const response = await fetch(toApiUrl('/auth/token/'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    credentials: 'omit',
    cache: 'no-store',
    body: JSON.stringify({
      email: safeEmail,
      username: safeEmail,
      password: safePassword,
    }),
  });

  if (!response.ok) {
    const detail = await readApiErrorMessage(response, `Login failed (${response.status})`);
    throw new Error(detail);
  }

  const data = await parseJsonOrThrow(response, 'Login');
  const accessToken = data?.access || null;
  const refreshToken = data?.refresh || null;

  if (accessToken) {
    setStoredTokens(accessToken, refreshToken);
    setForcePasswordChangePending(data?.force_password_change === true);
    recordLoginTime();
    return data;
  }

  if (data?.mfa_required === true && data?.login_token) {
    return data;
  }

  if (data?.registration_pending === true) {
    return data;
  }

  throw new Error('Login response is missing required authentication fields.');
}

export async function loginWithSsoCode(code) {
  const response = await fetch(toApiUrl('/auth/sso/exchange/'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'omit',
    cache: 'no-store',
    body: JSON.stringify({ code }),
  });

  if (!response.ok) {
    const detail = await readApiErrorMessage(response, `Google sign-in failed (${response.status})`, { clearAuthOn401: false });
    throw new Error(detail);
  }

  const data = await parseJsonOrThrow(response, 'Google sign-in');
  if (data?.access) {
    setStoredTokens(data.access, data.refresh || null);
    setForcePasswordChangePending(data?.force_password_change === true);
    recordLoginTime();
    return data;
  }
  if (data?.mfa_required === true && data?.login_token) {
    return data;
  }
  throw new Error('Google sign-in response is missing required authentication fields.');
}

async function postSignupJson(path, body, label) {
  const response = await fetch(toApiUrl(path), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    credentials: 'omit',
    cache: 'no-store',
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const parsed = await readApiErrorData(
      response,
      `${label} failed (${response.status}).`,
      { clearAuthOn401: false }
    );
    const error = new Error(parsed.message || `${label} failed (${response.status}).`);
    if (parsed.retryAfterSeconds) error.retryAfterSeconds = parsed.retryAfterSeconds;
    throw error;
  }

  return parseJsonOrThrow(response, label);
}

export async function initiateSignup({
  name,
  email,
  password,
  password2,
  organization,
  timezone = 'UTC',
  mobileNumber = '',
  referralCode = '',
}) {
  const safeEmail = String(email || '').trim().toLowerCase();
  const safeReferralCode = String(referralCode || '').trim();
  return postSignupJson(
    '/auth/signup/initiate/',
    {
      name: String(name || '').trim(),
      email: safeEmail,
      password: String(password || ''),
      password2: String(password2 || ''),
      organization: String(organization || '').trim(),
      timezone: String(timezone || 'UTC').trim() || 'UTC',
      mobile_number: String(mobileNumber || '').trim(),
      ...(safeReferralCode ? { referral_code: safeReferralCode } : {}),
    },
    'Signup'
  );
}

export async function requestSignupOtp({ email, channel }) {
  const safeEmail = String(email || '').trim().toLowerCase();
  const safeChannel = String(channel || 'email').trim().toLowerCase() === 'sms' ? 'sms' : 'email';
  return postSignupJson(
    '/auth/signup/otp/request/',
    { email: safeEmail, channel: safeChannel },
    'Verification code'
  );
}

export async function completeSignup({ email, challengeId, code }) {
  const data = await postSignupJson(
    '/auth/signup/complete/',
    {
      email: String(email || '').trim().toLowerCase(),
      challenge_id: String(challengeId || '').trim(),
      code: String(code || '').trim(),
    },
    'Signup'
  );

  if (data?.access) {
    setStoredTokens(data.access, data.refresh || null);
    recordLoginTime();
  }
  return data;
}

export async function requestPasswordReset(email) {
  const safeEmail = String(email || '').trim().toLowerCase();
  if (!safeEmail) {
    throw new Error('Enter your email address.');
  }

  const response = await fetch(toApiUrl('/auth/password-reset/'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    credentials: 'omit',
    cache: 'no-store',
    body: JSON.stringify({ email: safeEmail }),
  });

  if (response.ok) {
    let data = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }
    const message =
      data?.message ||
      'If an account exists for that email address, password reset instructions have been sent.';
    const resendCooldownSeconds = normalizePositiveSeconds(
      data?.resend_cooldown_seconds ?? data?.retry_after_seconds
    );
    return {
      message,
      resendCooldownSeconds,
      retryAfterSeconds: resendCooldownSeconds,
    };
  }

  const fallback =
    response.status === 429
      ? 'Too many reset requests. Please wait before trying again.'
      : `Password reset request failed (${response.status}).`;

  let detail = fallback;
  let retryAfterSeconds = null;
  try {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      const data = await response.json();
      retryAfterSeconds = normalizePositiveSeconds(
        data?.retry_after_seconds ?? data?.resend_cooldown_seconds
      );

      if (typeof data?.detail === 'string') {
        detail = data.detail;
      } else if (Array.isArray(data?.detail)) {
        detail = data.detail.map(String).join(' ');
      } else if (data?.non_field_errors) {
        const n = data.non_field_errors;
        detail = Array.isArray(n) ? n.join(' ') : String(n);
      } else if (data?.email != null) {
        const e = data.email;
        detail = `Email: ${Array.isArray(e) ? e.join(' ') : String(e)}`;
      }
    }
  } catch {
    // Fall back to generic message if error payload cannot be parsed.
  }

  const error = new Error(detail);
  if (retryAfterSeconds) {
    error.retryAfterSeconds = retryAfterSeconds;
  }
  throw error;
}

export async function confirmPasswordReset({ key, password1, password2 }) {
  const safeKey = String(key || '').trim();
  const safePassword1 = String(password1 || '');
  const safePassword2 = String(password2 || '');
  if (!safeKey || !safePassword1 || !safePassword2) {
    throw new Error('Reset key and both password fields are required.');
  }

  const response = await fetch(toApiUrl('/auth/password-reset/confirm/'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    credentials: 'omit',
    cache: 'no-store',
    body: JSON.stringify({
      key: safeKey,
      password1: safePassword1,
      password2: safePassword2,
    }),
  });

  if (!response.ok) {
    const fallback =
      response.status === 429
        ? 'Too many reset attempts. Please wait before trying again.'
        : `Password reset confirmation failed (${response.status}).`;
    const detail = await readApiErrorMessage(response, fallback, { clearAuthOn401: false });
    throw new Error(detail);
  }

  const data = await parseJsonOrThrow(response, 'Password reset confirm');
  return data?.message || 'Password has been reset successfully.';
}

export async function validatePasswordResetKey(key) {
  const safeKey = String(key || '').trim();
  if (!safeKey) {
    throw new Error('Missing reset key.');
  }

  const response = await fetch(toApiUrl('/auth/password-reset/key-status/'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    credentials: 'omit',
    cache: 'no-store',
    body: JSON.stringify({ key: safeKey }),
  });

  if (!response.ok) {
    const fallback = `Password reset key validation failed (${response.status}).`;
    const detail = await readApiErrorMessage(response, fallback, { clearAuthOn401: false });
    throw new Error(detail);
  }

  const data = await parseJsonOrThrow(response, 'Password reset key status');
  if (data?.valid !== true) {
    throw new Error(data?.detail || 'Invalid or expired password reset link.');
  }
  return data;
}

export async function acceptInvitation({ key, name, password, password2, timezone }) {
  const response = await fetch(toApiUrl('/auth/invitation/accept/'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    credentials: 'omit',
    cache: 'no-store',
    body: JSON.stringify({ key, name, password, password2, timezone }),
  });

  if (!response.ok) {
    const fallback = `Invitation acceptance failed (${response.status}).`;
    const detail = await readApiErrorMessage(response, fallback, { clearAuthOn401: false });
    throw new Error(detail);
  }

  const data = await parseJsonOrThrow(response, 'Invitation accept');
  if (data?.access) {
    localStorage.setItem('xspectre_access_token', data.access);
  }
  if (data?.refresh) {
    localStorage.setItem('xspectre_refresh_token', data.refresh);
  }
  return data;
}

export async function validateInvitationKey(key) {
  const safeKey = String(key || '').trim();
  if (!safeKey) {
    throw new Error('Missing invitation key.');
  }

  const response = await fetch(toApiUrl('/auth/invitation/key-status/'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    credentials: 'omit',
    cache: 'no-store',
    body: JSON.stringify({ key: safeKey }),
  });

  if (!response.ok) {
    const fallback = `Invitation key validation failed (${response.status}).`;
    const detail = await readApiErrorMessage(response, fallback, { clearAuthOn401: false });
    throw new Error(detail);
  }

  const data = await parseJsonOrThrow(response, 'Invitation key status');
  if (data?.valid !== true) {
    throw new Error(data?.detail || 'Invalid or expired invitation link.');
  }
  return data;
}

export async function verifyLoginMfaCode(loginToken, code) {
  const safeLoginToken = String(loginToken || '').trim();
  const safeCode = String(code || '').trim();
  if (!safeLoginToken || !safeCode) {
    throw new Error('Login token and verification code are required.');
  }

  const response = await fetch(toApiUrl('/auth/mfa/verify/'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    credentials: 'omit',
    cache: 'no-store',
    body: JSON.stringify({
      login_token: safeLoginToken,
      code: safeCode,
    }),
  });

  if (!response.ok) {
    const detail = await readApiErrorMessage(response, `MFA verification failed (${response.status})`, {
      clearAuthOn401: false,
    });
    throw new Error(detail);
  }

  const data = await parseJsonOrThrow(response, 'MFA verify');
  const accessToken = data?.access || null;
  const refreshToken = data?.refresh || null;
  if (!accessToken) {
    throw new Error('MFA verification response missing access token.');
  }

  setStoredTokens(accessToken, refreshToken);
  setForcePasswordChangePending(data?.force_password_change === true);
  recordLoginTime();
  return data;
}

export async function resendLoginMfaCode(loginToken) {
  const safeLoginToken = String(loginToken || '').trim();
  if (!safeLoginToken) {
    throw new Error('Login token is required.');
  }

  const response = await fetch(toApiUrl('/auth/mfa/resend/'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    credentials: 'omit',
    cache: 'no-store',
    body: JSON.stringify({ login_token: safeLoginToken }),
  });

  if (!response.ok) {
    const errData = await readApiErrorData(response, `MFA resend failed (${response.status})`, {
      clearAuthOn401: false,
    });
    const err = new Error(errData.message);
    err.errorCode = errData.errorCode;
    err.retryAfterSeconds = errData.retryAfterSeconds;
    throw err;
  }

  const data = await parseJsonOrThrow(response, 'MFA resend');
  return {
    message: data?.message || 'A new verification code was sent.',
    retryAfterSeconds: normalizePositiveSeconds(data?.retry_after_seconds),
  };
}

export async function sendMfaOtpChallenge({ mfaType, phoneNumber = '' }, token = null) {
  const normalizedType = normalizeMfaMethod(mfaType);
  if (!normalizedType) {
    throw new Error('MFA method is required.');
  }

  const payload = { mfa_type: normalizedType };
  const normalizedPhone = String(phoneNumber || '').trim();
  if (normalizedPhone) {
    payload.phone_number = normalizedPhone;
  }

  const response = await fetchWithAuthRetry(
    toApiUrl('/auth/otp/send/'),
    {
      method: 'POST',
      ...jsonFetchOptions(token),
      body: JSON.stringify(payload),
    },
    token
  );

  if (!response.ok) {
    const errData = await readApiErrorData(response, `MFA code send failed (${response.status})`);
    const err = new Error(errData.message);
    err.errorCode = errData.errorCode;
    err.retryAfterSeconds = errData.retryAfterSeconds;
    throw err;
  }

  return parseJsonOrThrow(response, 'MFA OTP send');
}

export async function verifyMfaOtpChallenge({ mfaType, challengeId, code, phoneNumber = '' }, token = null) {
  const normalizedType = normalizeMfaMethod(mfaType);
  const normalizedChallengeId = String(challengeId || '').trim();
  const normalizedCode = String(code || '').trim();
  if (!normalizedType || !normalizedChallengeId || !normalizedCode) {
    throw new Error('MFA method, challenge ID, and code are required.');
  }

  const payload = {
    mfa_type: normalizedType,
    challenge_id: normalizedChallengeId,
    code: normalizedCode,
  };

  const normalizedPhone = String(phoneNumber || '').trim();
  if (normalizedPhone) {
    payload.phone_number = normalizedPhone;
  }

  const response = await fetchWithAuthRetry(
    toApiUrl('/auth/otp/profile/verify/'),
    {
      method: 'POST',
      ...jsonFetchOptions(token),
      body: JSON.stringify(payload),
    },
    token
  );

  if (!response.ok) {
    const errData = await readApiErrorData(response, `MFA code verification failed (${response.status})`);
    const err = new Error(errData.message);
    err.errorCode = errData.errorCode;
    err.retryAfterSeconds = errData.retryAfterSeconds;
    throw err;
  }

  return parseJsonOrThrow(response, 'MFA OTP verify');
}

export async function getMfaStatus(token = null) {
  const response = await fetchWithAuthRetry(toApiUrl('/auth/mfa/status/'), jsonFetchOptions(token), token);
  if (!response.ok) {
    const detail = await readApiErrorMessage(response, `MFA status fetch failed (${response.status})`);
    throw new Error(detail);
  }
  return parseJsonOrThrow(response, 'MFA status');
}

export async function disableMfaMethod(method = 'active', password, token = null) {
  const safeMethod = normalizeMfaMethod(method) || 'active';
  const response = await fetchWithAuthRetry(
    toApiUrl('/auth/mfa/disable/'),
    {
      method: 'POST',
      ...jsonFetchOptions(token),
      body: JSON.stringify({ method: safeMethod, password }),
    },
    token
  );

  if (!response.ok) {
    const detail = await readApiErrorMessage(response, `MFA disable failed (${response.status})`);
    throw new Error(detail);
  }

  return parseJsonOrThrow(response, 'MFA disable');
}

export async function getTotpSetup(token = null) {
  const response = await fetchWithAuthRetry(
    toApiUrl('/auth/mfa/totp/setup/'),
    jsonFetchOptions(token),
    token
  );

  if (!response.ok) {
    const detail = await readApiErrorMessage(response, `TOTP setup failed (${response.status})`);
    const err = new Error(detail);
    err.status = response.status;
    throw err;
  }

  return parseJsonOrThrow(response, 'TOTP setup');
}

export async function activateTotpCode(secret, code, token = null) {
  const safeSecret = String(secret || '').trim();
  const safeCode = String(code || '').trim();
  if (!safeSecret || !safeCode) {
    throw new Error('TOTP secret and code are required.');
  }

  const response = await fetchWithAuthRetry(
    toApiUrl('/auth/mfa/totp/activate/'),
    {
      method: 'POST',
      ...jsonFetchOptions(token),
      body: JSON.stringify({ secret: safeSecret, code: safeCode }),
    },
    token
  );

  if (!response.ok) {
    const detail = await readApiErrorMessage(response, `TOTP activation failed (${response.status})`);
    throw new Error(detail);
  }

  return parseJsonOrThrow(response, 'TOTP activation');
}

export async function getMfaRecoveryCodes(token = null) {
  const response = await fetchWithAuthRetry(
    toApiUrl('/auth/mfa/recovery-codes/'),
    jsonFetchOptions(token),
    token
  );

  if (!response.ok) {
    const detail = await readApiErrorMessage(response, `Recovery code fetch failed (${response.status})`);
    throw new Error(detail);
  }

  return parseJsonOrThrow(response, 'MFA recovery codes');
}

export async function generateMfaRecoveryCodes(password, token = null) {
  const response = await fetchWithAuthRetry(
    toApiUrl('/auth/mfa/recovery-codes/generate/'),
    {
      method: 'POST',
      ...jsonFetchOptions(token),
      body: JSON.stringify({ password: String(password || '') }),
    },
    token
  );

  if (!response.ok) {
    const detail = await readApiErrorMessage(response, `Recovery code generation failed (${response.status})`);
    throw new Error(detail);
  }

  return parseJsonOrThrow(response, 'MFA recovery code generation');
}

export async function logoutUser(token = null) {
  const accessToken = token || getStoredAccessToken();
  const refreshToken = getStoredRefreshToken();

  debugApiLog('[LOGOUT] logoutUser called with accessToken:', !!accessToken, 'refreshToken:', !!refreshToken);

  const payload = {};
  if (refreshToken) {
    payload['refresh-token'] = refreshToken;
    payload.refresh_token = refreshToken;
  }

  try {
    debugApiLog('[LOGOUT] Making backend call to /auth/logout/');
    await fetch(toApiUrl('/auth/logout/'), {
      method: 'POST',
      headers: defaultHeaders(accessToken),
      credentials: getCredentialsMode(token),
      body: JSON.stringify(payload),
    });
  } finally {
    clearStoredTokens();
    clearLoginTime();
    clearAuthenticatedQueryCache();
  }
}

export const updatePassword = (payload, token = null) =>
  requestJson('POST', '/auth/update-pass/', payload, token);

export const forcePasswordChange = (payload, token = null) =>
  requestJson('POST', '/auth/force-password-change/', payload, token);

export const rejectInvitation = (key) => {
  return requestJson('POST', '/users/invitation-reject/', { key }, null);
};
